'use strict';

import * as THREE from 'three';
import { Points, Vector2, Vector3 } from 'three';

import { AsbuiltPoint } from '../../../../typings/api/integrations/leica/rest/v1/skymap-leica-connection';
import { DataTableColumns } from '../../react/components/data-table/data-table';
import { IntegrationAsbuiltPoint } from '../../react/components/integrations/state/integrations-state';
import { asbuiltContentColumns } from '../../react/components/skyview/panels/asbuilt/asbuilt-content-columns';
import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { isDefined } from '../../utils/variables';
import cameraManager from '../camera-manager';
import { AsbuiltGeometry } from '../geodata/asbuilt-geometry/asbuilt-geometry';
import { InputHandler } from '../input-handler';
import {
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { Tool } from './tool';

export class AsbuiltGeometryTool extends Tool {
  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void = undefined;

  private raycaster: THREE.Raycaster;
  private integrationPoint?: IntegrationAsbuiltPoint;

  constructor(inputHandler: InputHandler) {
    super('asbuilt-geometry', inputHandler);

    this.raycaster = new THREE.Raycaster();
  }

  onTooltipChanged(callback: (tooltip?: SkyviewAssetPositionProps) => void) {
    this.tooltipCallback = callback;
  }

  getMeta(sweref: string): MetaDefinition {
    return {
      src: 'tool.asbuilt-geometry',
      parameters: undefined,
    };
  }

  onTouchMove(e: TouchInputData) {
    return true;
  }

  onMouseWheel(e: MouseInputData) {
    this.pickAsbuiltGeometry(e.pointer.position);

    return true;
  }

  onMouseMove(e: MouseInputData) {
    this.pickAsbuiltGeometry(e.pointer.position);

    return true;
  }

  enable() {
    this.visible = true;
    this.inputHandler.setCursor('crosshair');
  }

  async disable() {
    this.visible = false;
    this.inputHandler.setCursor('');
    this.clearTooltip();
    return Promise.resolve();
  }

  updateTooltip(point?: Vector3) {
    const camera = cameraManager().getActiveCamera();
    if (!isDefined(point) || !isDefined(camera) || !isDefined(this.integrationPoint)) {
      return;
    }

    const screenPoint = camera.getScreenPosition(point);

    this.tooltipCallback?.({
      properties: {
        type: 'asbuilt-geometry',
        data: {
          columns: asbuiltContentColumns() as DataTableColumns<AsbuiltPoint>,
          items: [this.integrationPoint],
        },
      },
      screenPoint,
      coordinateSystem: '',
      coordinate: {
        easting: this.integrationPoint.x ?? 0,
        northing: this.integrationPoint.y ?? 0,
        height: this.integrationPoint.z ?? 0,
      },
    });
  }

  clearTooltip() {
    this.tooltipCallback?.();
  }

  private pickAsbuiltGeometry(pointerPosition: Vector2) {
    const asbuiltGeometry = AsbuiltGeometry.instance;
    const camera = cameraManager().getActiveCamera();
    if (!isDefined(camera) || !isDefined(asbuiltGeometry)) {
      return;
    }

    this.raycaster.setFromCamera(pointerPosition, camera.getTHREECamera());

    const intersectedPoints = this.raycaster
      .intersectObject(asbuiltGeometry, true)
      .filter((intersection) => intersection.object instanceof Points);
    if (intersectedPoints.length === 0) {
      this.clearTooltip();
      return;
    }

    const intersection = intersectedPoints[0];
    const asbuiltPointPosition = new Vector3().fromBufferAttribute(
      (intersection.object as Points).geometry.attributes.position,
      intersection.index ?? 0,
    );
    this.integrationPoint = asbuiltGeometry.getAsbuiltPoint(intersection.index) as
      | IntegrationAsbuiltPoint
      | undefined;

    this.updateTooltip(asbuiltPointPosition);
  }
}
